import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {FlowExecType} from '@mintdata-wsp/common/controllers/flow/FlowController';
import {FlowSimulation} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ButtonSize, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {DataSource} from '@mintdata/mint-kit/api/models/List';
import {ContextMenu, IContextMenuItem, ToggleOn} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./FlowTopBarRunButton.less');
const cx = classnames.bind(styles);

@observer
export class FlowTopBarRunButton extends React.Component<any, any>
{
    private _getRunClickHandler = (execType:FlowExecType):() => void =>
    {
        return () =>
        {
            const {dialogs, flow} = controllers.ui;

            if( !this._canRun() )
                return false;

            const openFlow = flow.getOpenFlow();
            const lastInputKeys = Array.from(openFlow.lastInputValues.keys());

            if( openFlow.orderedInputNames.length === 0 ||
                lastInputKeys.sort().toString() === openFlow.orderedInputNames.sort().toString() )
                flow.execFlowFromEditor(openFlow, openFlow.lastInputValues, execType);
            else
                dialogs.showDialog(Dialogs.FlowRunWithParameters, false);
        };
    };

    private _stopClickHandler = ():void =>
    {
        const {flow} = controllers.ui;

        const openFlow = flow.getOpenFlow();

        if( this._canStop() )
            flow.stopFlowFromEditor(openFlow);
    };

    private _menuProvider = (target:HTMLElement):DataSource<IContextMenuItem> =>
    {
        const {ui} = controllers;
        const {dialogs, flow} = ui;

        const runStreamingMenuItem:IContextMenuItem = {
            label: 'Run streaming',
            handler: this._getRunClickHandler(FlowExecType.Stream)
        };

        const runWithParametersMenuItem:IContextMenuItem = {
            label: 'Run with parameters...',
            handler: () => dialogs.showDialog(Dialogs.FlowRunWithParameters, false),
            isDisabled: !flow.getOpenFlow().inputs.length
        };

        return [runStreamingMenuItem, runWithParametersMenuItem];

    };

    private _renderMenu():JSX.Element
    {
        const menuTogglerId = 'runBtnMenu';

        return (
            <div id={menuTogglerId} className={cx('menu')}>
                <ContextMenu
                    toggleOn={[ToggleOn.CLICK]}
                    togglersIds={[menuTogglerId]}
                    popupAnchor={PopupAnchor.RightTop}
                    sourceAnchor={SourceAnchor.RightBottom}
                    popupClassName={cx('menuPopup')}
                    dataSource={this._menuProvider}
                >
                    <MDIcon
                        className={cx('menuIcon')}
                        name={MDIcons.common.ArrowDropdown}
                        size={MDIconSize.medium20}
                    />
                </ContextMenu>
            </div>
        );
    }

    private _getFlowSimulation():FlowSimulation
    {
        const {flow, flowSimulation} = controllers.ui;

        const openFlow = flow.getOpenFlow();

        return flowSimulation.getForFlowEditor(openFlow.id);
    }

    private _canRun():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return !simulation || (simulation && flowSimulation.isStoppedOrInitialNotRunning(simulation.id));
    }

    private _canStop():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isRunning(simulation.id);
    }

    private _isRunning():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isRunning(simulation.id);
    }

    private _isStarting():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isStarting(simulation.id);
    }

    private _isStopping():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isStopping(simulation.id);
    }

    public render():JSX.Element
    {
        const simulation = this._getFlowSimulation();
        const showStopBtn =
            simulation && simulation.execType === FlowExecType.Stream &&
            (this._isRunning() || this._isStopping());

        return (
            <div>
                {
                    showStopBtn ? (
                        <WSPButton
                            className={cx('stopBtn')}
                            isDisabled={this._isStopping()}
                            icon={MDIcons.common.Stop}
                            caption={'Stop'}
                            size={ButtonSize.Medium}
                            onClick={this._stopClickHandler}
                        />
                    ) : (
                        <div className={styles.container}>
                            <WSPButton
                                className={cx('runBtn')}
                                isDisabled={this._isStarting()}
                                caption={'Run'}
                                size={ButtonSize.Medium}
                                onClick={this._getRunClickHandler(FlowExecType.REST)}
                            />
                            {this._renderMenu()}
                        </div>
                    )
                }
            </div>

        );
    }
}
